import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroText, CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomTeaser } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface BulkIgnoreAtLaunchDialogProps {
	open: boolean;
	dataRoomGuid: string | undefined;
	selectedTeasers: string[];
	teasers: DataRoomTeaser[] | undefined;
	setTeasersSelected: (selected: string[]) => void;
	close: () => void;
}

export const BulkIgnoreAtLaunchDialog: React.FC<BulkIgnoreAtLaunchDialogProps> = ({
	open,
	dataRoomGuid,
	selectedTeasers,
	teasers,
	setTeasersSelected,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const sentTeasers = [] as DataRoomTeaser[];
	if (open) {
		selectedTeasers.map(selected => {
			const items = teasers?.filter(f => f.guid == selected && f.sentOn);
			if (items && items.length > 0) {
				sentTeasers.push(items[0]);
			}
		});
	}

	const onIgnoreAtLaunchConfirmation = () => {
		ignoreTeasers.mutate(selectedTeasers);
	};

	const ignoreTeasers = useMutation<string[], unknown, string[]>(
		async (values: string[]) => {
			const updatedTeasers = await getResponseData<string[]>(
				api.post<string[]>(CerebroApiEndpoints.Teasers.IgnoreAtLaunchMany(dataRoomGuid), values)
			);

			return updatedTeasers;
		},
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('Teaser(s) ingored at launch.', { variant: 'success' });

				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem ignoring the teaser(s) at launch.', { variant: 'error' });
			},
			onSettled: () => {
				setTeasersSelected([] as string[]);
			},
		}
	);

	if (open) {
		if (sentTeasers.length > 0) {
			return (
				<Dialog open={open} onClose={close}>
					<DialogTitle>Sent Teasers Can Not Be Ignored at Launch</DialogTitle>
					<DialogContent>
						<DialogContentText sx={{ color: '#000000' }}>
							<CerebroText sx={{ marginBottom: 2 }}>
								The following teasers cannot be ignored at launch because they have already been sent:
							</CerebroText>

							{sentTeasers.map(teaser => (
								<CerebroText key={teaser.guid}>
									{teaser.firstName} {teaser.lastName} at {teaser.bankName}
								</CerebroText>
							))}

							<CerebroText sx={{ marginTop: 2 }}>
								No teasers have been ignored at launch, Unselect the checkboxes for the sent teasers and
								then select the ignore at launch action.
							</CerebroText>
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<CerebroTextButton onClick={close}>Close</CerebroTextButton>
					</DialogActions>
				</Dialog>
			);
		} else {
			return (
				<Dialog open={open} onClose={close}>
					<DialogTitle>Confirm Ignore at Launch</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Are you sure you want to ignore at launch the {selectedTeasers.length} selected teasers?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<CerebroTextButton onClick={close}>No</CerebroTextButton>
						<CerebroTextButton onClick={onIgnoreAtLaunchConfirmation}>Yes</CerebroTextButton>
					</DialogActions>
				</Dialog>
			);
		}
	} else {
		return <></>;
	}
};
